import {
    AbstractControl, ValidationErrors, ValidatorFn
} from "@angular/forms";

import {
    CommonRegex
} from "@shopliftr/common-js/data";

import { DateTime } from "luxon";

/**
 * Determines if the input value is empty
 */
const isEmptyInputValue = (value: any): boolean => {

    return (!value && value !== 0 && value !== false);
};

/**
 * CommonValidators exposes a set of static functions that can be used to attach a ValidatorFn to a FormControl. The functions can
 * be referenced directly when used in Reactive Forms components. Each exposed function should also be wrapped in a directive that
 * is registered with the NG_VALIDATORS injection token. This enables the validator to also be used in Template-Driven Forms.
 */
export class CommonValidators {

    /**
     * Returns a ValidatorFn that validates a control's value against `pattern`.
     * On failure returns `{ pattern: { message } }` — an IMgdValidationErrorBody shape.
     * Use this instead of Validators.pattern() so mgd-error-message can display the error.
     */
    static patternMatcherValidator(pattern: RegExp | string, message: string): ValidatorFn {
        if (!pattern) {
            throw new Error("CommonValidators.patternMatcherValidator: pattern is required");
        }
        if (!message) {
            throw new Error("CommonValidators.patternMatcherValidator: message is required");
        }

        // assign function to a variable to avoid "Lambda not supported" errors in static functions
        const fn = (control: AbstractControl): ValidationErrors | null => {

            if (!control.value) {
                return null;
            }

            const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern);

            return regex.test(String(control.value)) ? null : { pattern: { message } };
        };

        return fn;
    }

    /**
     * Validator to ensure that an input value matches password Input value.
     */
    static confirmPasswordValidator(passwordControl: AbstractControl): ValidatorFn {

        const fn = (control: AbstractControl): ValidationErrors | null => {

            if (!passwordControl) {
                return { passwordMatch: { message: "passwords do not match" } };
            }

            return ((control.value === passwordControl.value) ? null : { passwordMatch: { message: "passwords do not match" } });
        };

        return fn;
    }

    /**
     * Validator to ensure that an input value matches valid email formatting rules. Also exposed to template-driven forms by
     * EmailValidatorDirective.
     */
    static emailValidator(enabled?: boolean): ValidatorFn {

        // assign function to a variable to avoid "Lambda not supported" errors in static functions
        const fn = (control: AbstractControl<string>): ValidationErrors | null => {

            if ((typeof enabled === "boolean") && !enabled) {
                return null;
            }

            if (isEmptyInputValue(control.value)) {
                return null;
            }

            return new RegExp(CommonRegex.EMAIL, "i").test(control.value) ? null : {
                email: {
                    message: "is not a valid email address"
                }
            };
        };

        return fn;
    }

    /**
     * Validator to ensure that an input value matches valid password formatting rules.
     */
    static passwordValidator(enabled?: boolean): ValidatorFn {

        const fn = (control: AbstractControl<string>): ValidationErrors | null => {

            if ((typeof enabled === "boolean") && !enabled) {
                return null;
            }

            if (isEmptyInputValue(control.value)) {
                return null;
            }

            if (!new RegExp(CommonRegex.PASSWORD).test(control.value)) {
                const message = {
                    passwordLowerCaseLetter: false,
                    passwordUpperCaseLetter: false,
                    passwordDigit: false,
                    passwordSymbol: false,
                    passwordLength: false
                };

                if (new RegExp(CommonRegex.CHARACTER_LOWERCASE).test(control.value)) {
                    message.passwordLowerCaseLetter = true;
                }

                if (new RegExp(CommonRegex.CHARACTER_UPPERCASE).test(control.value)) {
                    message.passwordUpperCaseLetter = true;
                }

                if (new RegExp(CommonRegex.DIGIT).test(control.value)) {
                    message.passwordDigit = true;
                }

                if (new RegExp(CommonRegex.SPECIAL_CHARACTER).test(control.value)) {
                    message.passwordSymbol = true;
                }

                if (control.value.length >= 8) {
                    message.passwordLength = true;
                }

                return message;
            }
            return null;
        };

        return fn;
    }

    static secureUrlValidator(enabled?: boolean): ValidatorFn {

        if ((typeof enabled === "boolean") && !enabled) {
            return null;
        }

        return CommonValidators.patternMatcherValidator(CommonRegex.URL_SECURE, "must be a valid secure URL (https://)");
    }

    /**
     * Validator to ensure a value is an absolute URL (http://, https:// or ftp://). An empty value is valid, so pair
     * it with `Validators.required` when a value is mandatory.
     *
     * @param enabled - Pass `false` to disable the validator.
     */
    static absoluteUrlValidator(enabled?: boolean): ValidatorFn {

        if ((typeof enabled === "boolean") && !enabled) {
            return null;
        }

        return CommonValidators.patternMatcherValidator(CommonRegex.ABSOLUTE_URL, "must be a valid URL, including http://, https:// or ftp://");
    }

    static urlValidator(enabled?: boolean): ValidatorFn {

        if ((typeof enabled === "boolean") && !enabled) {
            return null;
        }

        return CommonValidators.patternMatcherValidator(CommonRegex.URL, "must be a valid URL");
    }

    /**
     * Validator to ensure that the DateTime is greater than or equal to the provided DateTime
     */
    static minDateTimeValidator(minDateTime: DateTime): ValidatorFn {
        if (!minDateTime) {
            throw new Error("CommonValidators.minDateTimeValidator: minDateTime is required");
        }

        // assign function to a variable to avoid "Lambda not supported" errors in static functions
        const fn = (control: AbstractControl): ValidationErrors | null => {

            if (!control.value) {
                return null;
            }

            if (!(control.value instanceof DateTime)) {

                return {
                    notDateTime: {
                        message: "is not a DateTime object"
                    }
                };
            }
            if (control.value < minDateTime) {
                return {
                    minDateTime: {
                        message: `must be on or after ${minDateTime.toLocaleString(DateTime.DATE_MED)}`
                    }
                };
            }
            return null;
        };

        return fn;
    }

    /**
     * Returns a ValidatorFn that marks a control invalid when its value equals `rejectedValue`.
     * Designed to be added dynamically with `addValidator()` after an API rejection — auto-clears
     * when the user changes the field value.
     *
     * The error body follows {@link IMgdValidationErrorBody} so `mgd-error-message` renders it
     * automatically without per-field configuration.
     *
     * @param rejectedValue - The value that was rejected by the API.
     * @param message - The error message to display to the user.
     */
    static valueValidator(rejectedValue: string, message: string): ValidatorFn {

        const fn = (control: AbstractControl): ValidationErrors | null => {

            return control.value === rejectedValue ? { invalidData: { message } } : null;
        };

        return fn;
    }

    /**
     * Validator to ensure that the DateTime is less than or equal to the provided DateTime
     */
    static maxDateTimeValidator(maxDateTime: DateTime): ValidatorFn {
        if (!maxDateTime) {
            throw new Error("CommonValidators.maxDateTimeValidator: maxDateTime is required");
        }

        // assign function to a variable to avoid "Lambda not supported" errors in static functions
        const fn = (control: AbstractControl): ValidationErrors | null => {

            if (!control.value) {
                return null;
            }

            if (!(control.value instanceof DateTime)) {

                return {
                    notDateTime: {
                        message: "is not a DateTime object"
                    }
                };
            }
            if (control.value > maxDateTime) {
                return {
                    maxDateTime: {
                        message: `must be on or before ${maxDateTime.toLocaleString(DateTime.DATE_MED)}`
                    }
                };
            }
            return null;
        };

        return fn;
    }
}
